import { Group } from '@mantine/core';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { TravelReferenceDocumentsButtonLink } from '@/module/Travel/ReferenceDocument/components/TravelReferenceDocumentsButtonLink';
import { useTravelRequestFilters } from '@/module/Travel/Registry/useTravelRequestFilters';

import { AddButtonLink, Browse, NoContentPlaceholder, usePagination } from '@/core/components';

import { RoleType, useCurrentUser } from '@feature/security';
import { FilterBox, SearchFilter, useSearch, useSortDirection } from '@feature/utils';

import { TravelRequestCard } from './components/Card/TravelRequestCard';
import { TravelOwnDayBalanceChip } from './components/TravelOwnDayBalanceChip';
import { getTravelRequestPagedOptions } from './model/TravelRequestQuery';

export function TravelRequestBrowse() {
  const { t } = useTranslation();
  const { currentRole } = useCurrentUser();

  if (currentRole !== RoleType.AUTHOR) {
    return <NoContentPlaceholder title={t('travel.requests.noAccess')} />;
  }

  return <TravelRequestList />;
}

function TravelRequestList() {
  const { t } = useTranslation();
  const { sortDirection, toggleSortDirection } = useSortDirection('DESC');
  const { search, searchProps } = useSearch();
  const { pagination, useTotalElements, resetPagination, ...paginationProps } = usePagination({ search });
  // Drafts have no registration, so a preset year would hide them.
  const { filter, fields } = useTravelRequestFilters({
    initialYear: null,
    showStatuses: true,
    restrictDeanFaculty: true,
    yearDescription: t('travel.requests.filters.yearHidesDrafts'),
    onChange: resetPagination,
  });
  const { data } = useSuspenseQuery(
    getTravelRequestPagedOptions({
      ...pagination,
      ...filter,
      sortDirection,
      search,
    }),
  );
  useTotalElements(data);

  return (
    <Browse
      actions={
        <Group
          gap="xs"
          justify="flex-end"
        >
          <TravelReferenceDocumentsButtonLink visibleFrom="sm" />
          <AddButtonLink />
        </Group>
      }
      title={t('travel.requests.title')}
      header={
        <>
          {/* Beside the title a phone has no room for a second button, so there it gets its own row. */}
          <TravelReferenceDocumentsButtonLink
            hiddenFrom="sm"
            fullWidth
          />
          <TravelOwnDayBalanceChip />
        </>
      }
      pagination={paginationProps}
      filters={
        <FilterBox>
          <SearchFilter
            label={t('label.search')}
            description={t('travel.requests.searchBy')}
            {...searchProps}
          />
          {fields}
        </FilterBox>
      }
      sortDirection={sortDirection}
      sortedBy={t('travel.requests.sortedBy')}
      toggleSortDirection={toggleSortDirection}
      data={data.list}
      renderItem={request => (
        <TravelRequestCard
          key={request.id}
          request={request}
          to={String(request.id)}
        />
      )}
    />
  );
}
